import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-page SEO: unique title, meta description, canonical URL, Open Graph/Twitter
 * tags, and optional JSON-LD structured data (FAQPage, BreadcrumbList, Article…).
 * Falls back to the site-wide defaults in public/index.html.
 *
 * Usage:
 *   <SEO title="…" description="…" path="/features" />
 *   <SEO ... jsonLd={faqSchema} />                     // single schema
 *   <SEO ... jsonLd={[faqSchema, breadcrumbSchema]} /> // multiple
 */
export default function SEO({ title, description, path = '/', image, noindex = false, jsonLd }) {
  const canonical = `https://rakshaireland.com${path}`;
  const ogImage = image || 'https://rakshaireland.com/og-image.png';
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

// ── Helpers for common structured-data shapes ────────────────────────────────

/** BreadcrumbList schema for inner pages. Pass ordered [{ name, path }]. */
export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: `https://rakshaireland.com${it.path}`,
    })),
  };
}

/** FAQPage schema. Pass [{ q, a }]. */
export function faqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
